import React, {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {ColumnDef} from "@tanstack/react-table";
import dayjs from "dayjs";
import {DataTable} from "@/lib/components/DataTable";
import {QuoteSupplierRequesterDTO} from "@/api/data-contracts";
import {CommonI18N, ReportI18N} from "@/translations";
import {DateFormatter} from "@/lib/components/DateFormate";

interface SupplierRequesterReportProps {
    data: QuoteSupplierRequesterDTO[];
}

interface SupplierRequesterRow extends QuoteSupplierRequesterDTO {
    id: number;
}

/**
 * Report table showing supplier-requester relationship rows for quotes in the selected date range.
 */
const SupplierRequesterReport: React.FC<SupplierRequesterReportProps> = ({ data }) => {
    const { t } = useTranslation();
    const dateFormat = DateFormatter("date");

    const rows = useMemo<SupplierRequesterRow[]>(
        () => data.map((item, index) => ({ ...item, id: index })),
        [data]
    );

    const columns = useMemo<ColumnDef<SupplierRequesterRow>[]>(
        () => [
            {
                header: t(ReportI18N.page.column.supplier),
                accessorFn: (row) => row.supplierCompanyName ?? "",
                id: "supplierCompanyName"
            },
            {
                header: t(ReportI18N.page.column.requester),
                accessorFn: (row) => row.requesterCompanyName ?? "",
                id: "requesterCompanyName"
            },
            {
                header: t(ReportI18N.page.column.quoteStatus),
                accessorFn: (row) => (row.quoteStatus ? t(CommonI18N.enum.quoteStatus[row.quoteStatus]) : ""),
                id: "quoteStatus"
            },
            {
                header: t(ReportI18N.page.column.statusDate),
                accessorFn: (row) => (row.statusDate ? dayjs(row.statusDate).format(dateFormat) : ""),
                id: "statusDate"
            },
            {
                header: t(ReportI18N.page.column.requestReferenceNumber),
                accessorFn: (row) => row.requestCustomerReferenceNumber ?? "–",
                id: "requestCustomerReferenceNumber"
            },
            {
                header: t(ReportI18N.page.column.quoteReferenceNumber),
                accessorFn: (row) => row.quoteCustomerReferenceNumber ?? "–",
                id: "quoteCustomerReferenceNumber"
            }
        ],
        [t, dateFormat]
    );

    return <DataTable columns={columns} data={rows} noResultsValue="" striped maxHeight={940} />;
};

export default SupplierRequesterReport;
